/**
 * System Control Notification Service
 *
 * Manages WebSocket connections for system control real-time notifications using the
 * simplified notify_system() system from iris-be/app/pubsub/events.py.
 *
 * This service handles:
 * - Connection lifecycle (connect, disconnect, reconnect)
 * - Subscription management (subscribe/unsubscribe to pubsub groups)
 * - Event streaming with proper error handling
 * - Automatic reconnection with exponential backoff
 *
 * Backend Architecture:
 * - Backend uses notify_system(event, user_id, audience, payload) to send events
 * - Backend handles internal/external group routing automatically
 *
 * Usage:
 * ```typescript
 * // In a component
 * const notificationService = inject(SystemControlNotificationService);
 *
 * // Connect to the websocket
 * notificationService.connect().subscribe();
 *
 * // Listen for events
 * notificationService.events$.subscribe(event => {
 *   if (event.event === 'maintenance_mode_enabled') {
 *     // Handle maintenance mode enabled
 *   }
 * });
 *
 * // Disconnect when done
 * notificationService.disconnect();
 * ```
 */

import {
  Injectable,
  inject,
  DestroyRef,
  signal,
  computed,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, Subject, timer, EMPTY, throwError } from 'rxjs';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import {
  catchError,
  switchMap,
  tap,
  map,
  takeUntil,
  take,
} from 'rxjs/operators';
import environment from '@environments/environment';
import {
  ConnectionState,
  WebSocketConnectionInfo,
  SubscriptionResponse,
  SettingsCommunicationEvent,
  WebSocketConfig,
  WebSocketMessage,
  SettingsCommunicationEventType,
  isSystemMessage,
  isDataMessage,
} from './notification.types';
import { DEFAULT_WEBSOCKET_CONFIG } from './notification.constants';

@Injectable({ providedIn: 'root' })
export class SystemControlNotificationService {
  private readonly __http = inject(HttpClient);
  private readonly __destroyRef = inject(DestroyRef);

  // WebSocket connection
  private __socket$: WebSocketSubject<WebSocketMessage> | null = null;
  private readonly __disconnect$ = new Subject<void>();

  // Event stream - use Subject to prevent replay of old events on resubscribe
  private readonly _events$ = new Subject<SettingsCommunicationEvent>();
  public readonly events$: Observable<SettingsCommunicationEvent> =
    this._events$.asObservable();

  // State management using signals
  private readonly $connectionState = signal<ConnectionState>(
    ConnectionState.DISCONNECTED,
  );
  private readonly $connectionId = signal<string | null>(null);
  private readonly $reconnectAttempts = signal<number>(0);

  // Public computed signals
  public readonly connectionState = this.$connectionState.asReadonly();
  public readonly isConnected = computed(
    () => this.connectionState() === ConnectionState.CONNECTED,
  );
  public readonly reconnectAttempts = this.$reconnectAttempts.asReadonly();

  // Configuration
  private __config: WebSocketConfig = DEFAULT_WEBSOCKET_CONFIG;

  constructor() {
    // Clean up on service destruction
    this.__destroyRef.onDestroy(() => {
      this.disconnect();
    });
  }

  /**
   * Update WebSocket configuration
   */
  setConfig(config: Partial<WebSocketConfig>): void {
    this.__config = { ...this.__config, ...config };
  }

  /**
   * Connect to the WebSocket and subscribe to the notification group.
   *
   *
   * Uses settings notification endpoints:
   *  - POST /settings/notifications/connect/ - Get WebSocket URL with token
   *  - POST /settings/notifications/subscribe/ - Subscribe to group
   *
   * Backend automatically routes to correct group (internal-{id} or external-{id})
   *
   * @returns Observable that completes when connected and subscribed
   */
  public connect(): Observable<void> {
    // If already connected to this workspace, do nothing
    if (this.isConnected()) {
      console.log('[SettingsCommunication] Already connected');
      return EMPTY;
    }

    this.$connectionState.set(ConnectionState.CONNECTING);
    this.$reconnectAttempts.set(0);

    const connectionId = this.__generateConnectionId();
    this.$connectionId.set(connectionId);

    console.log(
      `[SettingsCommunication] Connecting with connection ID: ${connectionId}`,
    );

    return this.__establishConnection(connectionId).pipe(
      tap(() => {
        this.$connectionState.set(ConnectionState.CONNECTED);
        this.$reconnectAttempts.set(0);
        console.log('[SettingsCommunication] Successfully connected');
      }),
      catchError((error) => {
        console.error('[SettingsCommunication] Failed to connect');
        this.__handleConnectionError();
        return throwError(() => error);
      }),
      takeUntilDestroyed(this.__destroyRef),
    );
  }

  /**
   * Disconnect from the current workspace
   */
  disconnect(): void {
    const connectionId = this.$connectionId();

    if (connectionId) {
      console.log(`[SettingsCommunication] Unsubscribing`);
      // Unsubscribe from workspace group
      this.__unsubscribeFromConnection(connectionId)
        .pipe(take(1))
        .subscribe({
          next: () =>
            console.log('[SettingsCommunication] Successfully unsubscribed'),
          error: (err) =>
            console.warn('[SettingsCommunication] Error unsubscribing:', err),
        });
    }

    // Close WebSocket connection
    this.__disconnect$.next();
    if (this.__socket$) {
      this.__socket$.complete();
      this.__socket$ = null;
    }

    // Reset state
    this.$connectionState.set(ConnectionState.DISCONNECTED);
    this.$connectionId.set(null);
    this.$reconnectAttempts.set(0);
  }

  /**
   * Reconnect to receive notifications
   * Used internally for automatic reconnection with exponential backoff
   */
  private __reconnect(): Observable<void> {
    const attempts = this.$reconnectAttempts();

    if (attempts >= this.__config.maxReconnectAttempts) {
      console.error(
        `[SettingsCommunication] Max reconnection attempts (${this.__config.maxReconnectAttempts}) reached`,
      );
      this.$connectionState.set(ConnectionState.ERROR);
      return throwError(() => new Error('Max reconnection attempts reached'));
    }

    this.$connectionState.set(ConnectionState.RECONNECTING);
    this.$reconnectAttempts.update((val) => val + 1);

    // Exponential backoff: 1s, 2s, 4s, 8s, 16s, capped at maxReconnectDelayMs
    const delayMs = Math.min(
      this.__config.reconnectDelayMs * Math.pow(2, attempts),
      this.__config.maxReconnectDelayMs,
    );

    console.log(
      `[SettingsCommunication] Reconnecting in ${delayMs}ms (attempt ${attempts + 1}/${this.__config.maxReconnectAttempts})`,
    );

    return timer(delayMs).pipe(
      switchMap(() => {
        const connectionId = this.__generateConnectionId();
        this.$connectionId.set(connectionId);
        return this.__establishConnection(connectionId);
      }),
      tap(() => {
        this.$connectionState.set(ConnectionState.CONNECTED);
        this.$reconnectAttempts.set(0);
        console.log('[SettingsCommunication] Successfully reconnected');
      }),
      catchError((error) => {
        console.error(
          '[SettingsCommunication] Reconnection attempt failed:',
          error,
        );
        // Try again recursively
        return this.__reconnect();
      }),
    );
  }

  /**
   * Establish WebSocket connection and subscribe to group
   *
   * Steps:
   * 1. POST /settings/notifications/connect/ - Get WebSocket URL with embedded token
   * 2. Connect to WebSocket URL
   * 3. POST /settings/notifications/subscribe/ - Subscribe to group
   */
  private __establishConnection(connectionId: string): Observable<void> {
    return this.__getConnectionUrl(connectionId).pipe(
      switchMap((connectionInfo) => {
        // Create a subject to signal when subscription is complete
        const subscriptionComplete$ = new Subject<void>();

        // Create WebSocket connection
        this.__socket$ = webSocket<WebSocketMessage>({
          url: connectionInfo.url,
          openObserver: {
            next: () => {
              console.log(
                '[SettingsCommunication] WebSocket connection opened',
              );
              // NOW subscribe to workspace group after connection is established
              this.__subscribeToConnection(connectionId)
                .pipe(take(1))
                .subscribe({
                  next: () => {
                    console.log(
                      '[SettingsCommunication] Subscription completed after WebSocket opened',
                    );
                    subscriptionComplete$.next();
                    subscriptionComplete$.complete();
                  },
                  error: (err) => {
                    console.error(
                      '[SettingsCommunication] Failed to subscribe after connection:',
                      err,
                    );
                    subscriptionComplete$.error(err);
                  },
                });
            },
          },
          closeObserver: {
            next: () => {
              console.log(
                '[SettingsCommunication] WebSocket connection closed',
              );
              this.__handleConnectionClosed();
            },
          },
        });

        // Listen to messages
        this.__socket$
          .pipe(
            takeUntil(this.__disconnect$),
            takeUntilDestroyed(this.__destroyRef),
          )
          .subscribe({
            next: (message) => this.__handleMessage(message),
            error: (error) => {
              console.error('[SettingsCommunication] WebSocket error:', error);
              this.__handleConnectionError();
            },
          });

        // Wait for subscription to complete before resolving
        return subscriptionComplete$.asObservable();
      }),
    );
  }

  /**
   * Get WebSocket connection URL from backend
   * Uses endpoint:
   * POST /settings/notifications/connect/
   */
  private __getConnectionUrl(
    connectionId: string,
  ): Observable<WebSocketConnectionInfo> {
    return this.__http.post<WebSocketConnectionInfo>(
      `${environment.irisApi}/settings/notifications/connect/`,
      { connection_str: connectionId },
    );
  }

  /**
   * Subscribe to notification group
   * Uses endpoint:
   * POST /settings/notifications/subscribe/
   *
   * Backend automatically subscribes to:
   * - internal-workspace-{id} for internal users
   * - external-workspace-{id} for external users
   */
  private __subscribeToConnection(connectionId: string): Observable<void> {
    return this.__http
      .post<SubscriptionResponse>(
        `${environment.irisApi}/settings/notifications/subscribe/`,
        { connection_str: connectionId },
      )
      .pipe(
        tap((response) => {
          console.log(
            '[SettingsCommunication] Subscribed to notifications:',
            response,
          );
          if (response.error) {
            console.warn(
              '[SettingsCommunication] Subscription warning:',
              response.error,
            );
          }
        }),
        map(() => undefined),
      );
  }

  /**
   * Unsubscribe from notification group
   * Uses endpoint:
   * POST /settings/notifications/unsubscribe/
   */
  private __unsubscribeFromConnection(connectionId: string): Observable<void> {
    const _initiatedLogout = sessionStorage.getItem('initiatedLogout');
    if (_initiatedLogout === 'true') {
      return EMPTY;
    }
    return this.__http
      .post<SubscriptionResponse>(
        `${environment.irisApi}/settings/notifications/unsubscribe/`,
        { connection_str: connectionId },
      )
      .pipe(
        tap((response) => {
          console.log(
            '[SettingsCommunication] Unsubscribed from notifications:',
            response,
          );
        }),
        map(() => undefined),
        catchError((error) => {
          console.error('[SettingsCommunication] Error unsubscribing:', error);
          return EMPTY;
        }),
      );
  }

  /**
   * Handle incoming WebSocket messages
   *
   * Azure Web PubSub sends messages in different formats:
   * - System messages: { type: "system", event: "connected" }
   * - Group messages: The actual notify_system() payload
   */
  private __handleMessage(message: WebSocketMessage): void {
    console.log('[SettingsCommunication] Received WebSocket message:', message);

    // Ignore system messages using type guard
    if (isSystemMessage(message)) {
      console.log('[SettingsCommunication] System message:', message.event);
      return;
    }

    // Use type guard to narrow to data message
    if (isDataMessage(message)) {
      const event: SettingsCommunicationEvent = {
        event: message.event,
        initiating_user_id: message.initiating_user_id,
        timestamp: message.timestamp,
        target_user_id: message.target_user_id,
      };

      // Validate event type (extra safety check)
      const validEvents: SettingsCommunicationEventType[] = [
        'maintenance_mode_enabled',
        'maintenance_mode_disabled',
        'maintenance_mode_scheduled',
        'maintenance_mode_cancelled',
        'logout_user',
      ];
      if (validEvents.includes(event.event)) {
        this._events$.next(event);
      } else {
        console.warn(
          '[SettingsCommunication] Unknown event type:',
          event.event,
        );
      }
    } else {
      console.warn(
        '[SettingsCommunication] Invalid message structure:',
        message,
      );
    }
  }

  /**
   * Handle connection closed
   */
  private __handleConnectionClosed(): void {
    if (this.$connectionState() === ConnectionState.CONNECTED) {
      console.log(
        '[SettingsCommunication] Connection closed unexpectedly, attempting to reconnect...',
      );
      this.__reconnect()
        .pipe(take(1))
        .subscribe({
          error: (err) =>
            console.error('[SettingsCommunication] Failed to reconnect:', err),
        });
    }
  }

  /**
   * Handle connection error
   */
  private __handleConnectionError(): void {
    if (this.$reconnectAttempts() < this.__config.maxReconnectAttempts) {
      this.__reconnect()
        .pipe(take(1))
        .subscribe({
          error: (err) =>
            console.error('[SettingsCommunication] Failed to reconnect:', err),
        });
    } else {
      this.$connectionState.set(ConnectionState.ERROR);
    }
  }

  /**
   * Generate a unique connection ID for this client session
   * Format: timestamp-random (e.g., "1699300000000-abc123xyz")
   */
  private __generateConnectionId(): string {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 15);
    return `${timestamp}-${random}`;
  }
}
